import { CommonModule } from '@angular/common'
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms'
import { IonicModule } from '@ionic/angular'
import { TranslocoModule } from '@jsverse/transloco'
import { TooltipModule } from '@syncfusion/ej2-angular-popups'
import { TimePickerModule } from '@syncfusion/ej2-angular-calendars'
import { MenuNotificationDotDirective } from '../../../features/notification/menuNotificationDotDirective/menu-notification-dot.directive'
import { ColumnDef, ColumnSelectComponent } from '../../../components/column-select/column-select.component'
import { AfterViewInit, Component, inject, OnInit } from '@angular/core'
import { Observable, of } from 'rxjs'
import { ConfigTooltipTexts } from '../../../features/config/ConfigTooltipTexts'
import { catchError, debounceTime, map, takeUntil } from 'rxjs/operators'
import { LoginService } from '../../../services/login/login.service'
import { CustomUiService } from '../../../features/custom-ui/custom-ui.service'
import { Store } from '@ngxs/store'
import { UserState } from '../../../store'
import { readFileAsBase64 } from '../../../shared/utils/fileUtils'
import { Config } from '../../../features/config/Config.model'
import { ConfigKeys } from '../../../features/config/ConfigKeys'
import { ConfigService } from '../../../services/config.service'
import { ColumnConfigService } from '../../../services/column-config.service'
import { FileUploadService } from '../../../services/file-upload.service'

export enum ConfigTabType {
  Obecne = 'obecne',
  OA = 3,
  PS = 4,
}

@Component({
  selector: 'app-config-management',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    IonicModule,
    TranslocoModule,
    TooltipModule,
    TimePickerModule,
    MenuNotificationDotDirective,
    ColumnSelectComponent,
  ],
  templateUrl: './config-management.page.html',
  styleUrls: ['./config-management.page.scss'],
})
export class ConfigManagementPage implements OnInit, AfterViewInit {
  private configService = inject(ConfigService)
  private columnConfigService = inject(ColumnConfigService)
  private customUiService = inject(CustomUiService)
  private fb = inject(UntypedFormBuilder)
  private loginService = inject(LoginService)
  private store = inject(Store)
  private fileUploadService = inject(FileUploadService)

  configKeys: typeof ConfigKeys = ConfigKeys
  tooltipTexts = ConfigTooltipTexts

  config$: Observable<Config[]>
  configs: Config[] = []

  form: UntypedFormGroup = this.fb.group({})

  activeConfigs: ConfigKeys[] = [
    ConfigKeys.ODKDY_JE_POZDE_OBJEDNANA_ASISTENCE_HODINY,
    ConfigKeys.DO_KDY_MUSI_ASISTENT_POTVRDIT_ASISTENCI,
    ConfigKeys.ODKDY_JE_POZDE_ZRUSENA_JIZ_POTVRZENA_ASISTENCE_HODINY,
    ConfigKeys.POZDE_OBJEDNANA_JESTLI_SE_VZTAHUJE_NA_KOORDINATORY,
    ConfigKeys.DOKDY_JE_MOZNA_EDITACE_PROBEHLYCH_ASISTENCI_DNY,
    ConfigKeys.ORDINACNI_HODINY_START_NO_TZ,
    ConfigKeys.ORDINACNI_HODINY_END_NO_TZ,
    ConfigKeys.STORNO_BEZ_LIMITU_O_VIKENDECH_A_SVATCICH,
    ConfigKeys.VYCHOZI_DOBA_PREJEZDU_MINUTY,
    ConfigKeys.ASSISTANT_LIST_CUSTOM_COLUMNS,

    ConfigKeys.ORGANIZACE_LOGO,
    ConfigKeys.ORGANIZACE_ICO,
    ConfigKeys.ORGANIZACE_BANKA,
    ConfigKeys.ORGANIZACE_NAZEV,
    ConfigKeys.ORGANIZACE_ADRESA_SIDLA,
    ConfigKeys.ORGANIZACE_CISLO_UCTU,
    ConfigKeys.ORGANIZACE_TELEFON,

    ConfigKeys.JOURNAL_ENTRY_HISTORY_ENABLED,
  ]

  availableCustomColumns: { [key: string]: ColumnDef[] } = {}
  configuredCustomColumns: { [key: string]: Observable<string[]> } = {}

  ConfigTabType = ConfigTabType
  visibleTabs$: Observable<(ConfigTabType | string)[]>
  selectedTab: ConfigTabType | string = ConfigTabType.Obecne

  logo: { file: File | null; base64: string | '' } = { file: null, base64: '' }

  constructor() {
    this.availableCustomColumns.assistantList = this.customUiService
      .getAvailableCustomColumns('assistantList')
      .map((c) => ({ id: c.id, name: c.columnName }))
    this.configuredCustomColumns.assistantList = this.customUiService.getConfiguredCustomColumns('assistantList').pipe(
      map((dataSources) => dataSources.map((o) => o.id)),
      catchError((error) => {
        console.error('Error loading configured custom columns for assistantList:', error)
        return of([])
      }),
    )
  }

  ngOnInit() {
    this.initForm()
    this.config$ = this.configService.getAllWithCaching().pipe(takeUntil(this.loginService.logout$))
    this.visibleTabs$ = this.store.select(UserState.getConfigTabsForActiveAllocation)
  }

  ngAfterViewInit(): void {
    this.config$.pipe(takeUntil(this.loginService.logout$)).subscribe((configs) => {
      this.configs = configs
      for (const config of configs) {
        const parsedValue = this.getParsedConfigValue(config)
        if (!this.form.controls[config.name]?.dirty) {
          this.form.patchValue(
            {
              [config.name]: this.getParsedConfigValue(config),
            },
            { emitEvent: false },
          )
        }
        if (config.name === ConfigKeys.ORGANIZACE_LOGO && parsedValue) {
          this.logo.base64 = parsedValue
        }
      }
    })
  }

  private getParsedConfigValue(config: Config) {
    switch (config.name) {
      case ConfigKeys.ASSISTANT_LIST_CUSTOM_COLUMNS:
        return ColumnConfigService.parseConfigValue(config.value)
      default:
        return config.value
    }
  }

  private initForm() {
    const control = {}
    this.activeConfigs.map((c) => (control[c] = []))
    this.form = this.fb.group(control)
    this.form.addControl('logoFileControl', this.fb.control(null, [this.logoValidator.bind(this)]))
    this.updateConfigOnValueChanges(this.activeConfigs)
  }

  updateConfig(key: ConfigKeys, value) {
    const changedConfig = this.configs.filter((c) => c.name === key)[0]
    let storeValue
    switch (key) {
      case ConfigKeys.ASSISTANT_LIST_CUSTOM_COLUMNS:
        this.columnConfigService.updateForControl('assistantList', value)
        break
      default:
        storeValue = value
        break
    }
    if (storeValue !== undefined) {
      changedConfig.value = storeValue
      this.configService.update(changedConfig)
    }
  }

  private updateConfigOnValueChanges(configs: ConfigKeys[]) {
    for (const config of configs) {
      this.form
        .get(config)
        .valueChanges.pipe(debounceTime(500), takeUntil(this.loginService.logout$))
        .subscribe((newValue) => {
          this.updateConfig(config, newValue)
        })
    }
  }

  async handleLogoFile(file: File | null) {
    if (!file) return this.removeLogo()

    const control = this.form.get('logoFileControl')
    control.setValue(file)
    control.updateValueAndValidity()

    const base64 = await readFileAsBase64(file)

    if (control.valid) {
      this.logo = { file, base64 }
      try {
        const uploaded = await this.fileUploadService.upload(file)
        await this.updateConfig(ConfigKeys.ORGANIZACE_LOGO, uploaded.uri)
      } catch (err) {
        console.error('Logo upload failed', err)
      }
    } else {
      console.warn('The file failed validation, upload canceled')
    }
  }

  logoValidator(control: any) {
    const file: File = control.value
    if (!file) return null
    const validTypes = ['image/png', 'image/jpeg', 'image/gif']
    const maxSizeMB = 2
    if (!validTypes.includes(file.type)) {
      return { logoError: 'Jsou povoleny pouze obrázky ve formátu PNG a JPEG' }
    }
    if (file.size / 1024 / 1024 > maxSizeMB) {
      return { logoError: 'Velikost souboru nesmí překročit 2 MB' }
    }
    return null
  }

  async onLogoFileChange(event: Event) {
    const input = event.target as HTMLInputElement
    if (input.files?.length) {
      await this.handleLogoFile(input.files[0])
    }
  }

  removeLogo() {
    this.logo.base64 = null
    const control = this.form.get('logoFileControl')
    control.setValue(null)
    this.updateConfig(ConfigKeys.ORGANIZACE_LOGO, '')
  }
}
